import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { apiClient } from "../utils/http";
import { useSession } from "./SessionContext";
import { LIVE_ORDER_STATUSES, ORDER_TYPE, OFFLINE_PAYMENT_OPTION, STORAGE_KEYS } from "../utils/constants";

const OrderContext = createContext(null);

// The last order seen live in this QR session — see STORAGE_KEYS.lastOrder.
// Survives a reload (and the tab being closed and reopened mid-meal), so the
// screen still knows a meal happened once the till has closed the order and
// my-orders no longer returns anything live.
function readLastOrder() {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.lastOrder);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function rememberLastOrder(order) {
  const value = { id: order.id, order_no: order.order_no ?? null, at: Date.now() };
  try {
    localStorage.setItem(STORAGE_KEYS.lastOrder, JSON.stringify(value));
  } catch {
    // private mode: this session only
  }
  return value;
}

// This app deliberately keeps exactly one order in view at a time — the
// guest's own cart (status 'created', not yet sent to the kitchen) before
// checkout, and after that the one live order for their table (status
// 'placed' -> optionally 'modifying' while adding more -> 'dispatched').
// There's no order list/history screen, matching the "only one live order"
// requirement — so this context tracks a single `order`, not a collection.
export function OrderProvider({ children }) {
  const { ready, tableKey, table, contactNumber, customerName, saveContactDetails } = useSession();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [lastOrder, setLastOrder] = useState(readLastOrder);

  // GET v1/my-orders (OrderController@pastOrders) returns every non-cart
  // order newest first — the first one still in a live status is this
  // table's current order. GET v1/my-profile (UserController@getUserDetails)
  // is the only way to recover a not-yet-placed cart (status 'created'),
  // since pastOrders excludes that status entirely — so it's only consulted
  // when no live order was found.
  //
  // `quiet`: re-check without blanking the screen first. The status moves on
  // at the till, not here (bill printed, order closed), and nothing pushes
  // that to this app — so the Live Order screen re-checks whenever the guest
  // opens or returns to it, and a spinner over an order they're already
  // reading would be the wrong way to show it.
  const refresh = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    setError("");
    try {
      const ordersRes = await apiClient.get("my-orders");
      const paginator = ordersRes.data?.orders || {};
      const live = (paginator.data || []).find((o) => LIVE_ORDER_STATUSES.includes(o.status));
      if (live) {
        setOrder(live);
        setLastOrder(rememberLastOrder(live));
        return live;
      }
      const profileRes = await apiClient.get("my-profile");
      const cart = profileRes.data?.cart || null;
      setOrder(cart);
      return cart;
    } catch (err) {
      setError(err.message || "Failed to load your order");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (ready) refresh();
  }, [ready, refresh]);

  // POST v1/add-to-cart — OrderController@addToCart. Passing the current
  // order's id lets the backend find/update it; omitting it (order === null,
  // i.e. this is the very first item) makes the backend create a fresh
  // 'created' cart. quantity <= 0 deletes the line — used by removeItem/
  // updateItemQuantity below instead of a separate endpoint (updateCartItems
  // only accepts a 'created' order, which would break once the order is
  // live/'modifying' — addToCart accepts both).
  const addItem = useCallback(
    async ({ product, option, addons, quantity, notes, itemId }) => {
      const res = await apiClient.post("add-to-cart", {
        id: order?.id,
        item_id: itemId || null,
        product: product.id,
        quantity,
        option: option?.id || "",
        addons: addons && addons.length > 0 ? JSON.stringify(addons) : "",
        notes: notes || "",
      });
      setOrder(res.data?.order || null);
      return res.data?.order;
    },
    [order?.id]
  );

  const updateItemQuantity = useCallback(
    (item, quantity) =>
      addItem({
        product: item.product,
        option: item.option?.id ? item.option : null,
        addons: item.addons,
        quantity,
        notes: item.notes,
        itemId: item.id,
      }),
    [addItem]
  );

  const removeItem = useCallback((item) => updateItemQuantity(item, 0), [updateItemQuantity]);

  // POST v1/modify-order — flips a live 'placed'/'dispatched' order back to
  // 'modifying' so addToCart will accept changes against it again (it only
  // matches orders in ['modifying','created'] — see OrderController@addToCart).
  const beginEdit = useCallback(async () => {
    if (!order || order.status === "modifying") return order;
    await apiClient.post("modify-order", { id: order.id });
    return refresh();
  }, [order, refresh]);

  // POST v1/cancel-modify — releases the 'modifying' lock without changing
  // anything further; used when the guest opens "Add more items" and then
  // backs out without actually adding anything.
  const cancelEdit = useCallback(async () => {
    if (!order || order.status !== "modifying") return order;
    await apiClient.post("cancel-modify", { id: order.id });
    return refresh();
  }, [order, refresh]);

  // POST v1/checkout — OrderController@placeOrder. Used both for the very
  // first placement (status 'created' -> 'placed') and for saving edits made
  // to an already-live order (status 'modifying' -> 'placed' again) — the
  // backend takes the same request either way. `table` must be the numeric
  // store_tables.id (it's assigned straight to the store_table_id FK column
  // with no lookup on the backend) — the QR's ?table= only gives us that
  // table's `key`, so SessionContext's `table` (resolved by matching that key
  // against the store's own store_tables list) is what actually gets sent,
  // not the raw key. name/contact are optional for dine_in (no one to take a
  // phone number from at the table), so an empty value is sent through as
  // null rather than blocked here.
  const submitOrder = useCallback(
    async ({ name, contact, orderNote } = {}) => {
      if (!order) throw new Error("Nothing to order yet");
      if (!tableKey) throw new Error("No table found — please rescan the QR code on your table");
      if (!table) throw new Error("This table's QR code doesn't match any table for this store — please rescan it");
      const finalContact = contact || contactNumber;
      const finalName = name || customerName;

      setSubmitting(true);
      try {
        const res = await apiClient.post("checkout", {
          id: order.id,
          order_type: ORDER_TYPE,
          table: table.id,
          contact_number: finalContact || null,
          offline_payment_option: OFFLINE_PAYMENT_OPTION,
          name: finalName || null,
          order_note: orderNote ?? order.order_note ?? "",
        });
        if (finalContact || finalName) saveContactDetails(finalName, finalContact);
        await refresh();
        return res.data?.order;
      } finally {
        setSubmitting(false);
      }
    },
    [order, tableKey, table, contactNumber, customerName, saveContactDetails, refresh]
  );

  const isLive = !!order && LIVE_ORDER_STATUSES.includes(order.status);
  const isCart = order?.status === "created";

  return (
    <OrderContext.Provider
      value={{
        order,
        isLive,
        isCart,
        lastOrder,
        loading,
        error,
        submitting,
        refresh,
        addItem,
        updateItemQuantity,
        removeItem,
        beginEdit,
        cancelEdit,
        submitOrder,
      }}
    >
      {children}
    </OrderContext.Provider>
  );
}

export function useOrder() {
  const ctx = useContext(OrderContext);
  if (!ctx) throw new Error("useOrder must be used within OrderProvider");
  return ctx;
}
